import { type ForgeExtensionPoints, type ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import { type getForgeUIExtensionsAsync } from '../../../provider/getForgeUIExtensionsAsync';
import { type ViewContext } from '../../iframe/types';

type GetForgeUIExtensionsAsync = typeof getForgeUIExtensionsAsync;

type FullForgeUIExtensionType = ForgeUIExtensionType & {
	properties: ForgeUIExtensionType['properties'] & {
		route?: string;
	};
};

type TargetURLUtils = {
	getContext: () => Promise<ViewContext>;
	getAppForgeUIExtensions: (
		param: Omit<
			Parameters<GetForgeUIExtensionsAsync>[0],
			'client' | 'contextIds' | 'moduleType' | 'extensionsFilter'
		>,
	) => ReturnType<GetForgeUIExtensionsAsync>;
};

const makeConfluenceURLPath = (basePath: string, parameters?: Record<string, string>) => {
	const path = `/wiki${basePath}`;

	if (parameters && Object.keys(parameters).length > 0) {
		const paramsString = new URLSearchParams(parameters).toString();
		return `${path}?${paramsString}`;
	}

	return path;
};

const extensionURLHelpers: Partial<
	Record<ForgeExtensionPoints, (extension: FullForgeUIExtensionType) => string | null>
> = {
	'confluence:globalPage': ({ properties, environmentId, appId }) => {
		const { route } = properties;
		if (appId && environmentId && route) {
			return makeConfluenceURLPath(`/apps/${appId}/${environmentId}/${route}`);
		}
		return null;
	},
	'confluence:globalSettings': ({ id }) => makeConfluenceURLPath('/admin/forge', { id }),
};

const navigationTargetHandlers = {
	contentView: async ({ contentId, version }: { contentId: string; version?: number }) => {
		const resp = await fetch(`/wiki/rest/api/content/${contentId}`);
		const {
			id,
			_links: { webui },
		} = await resp.json();

		if (version && version >= 0) {
			return makeConfluenceURLPath('/pages/viewpage.action', {
				pageId: id,
				pageVersion: `${version}`,
			});
		} else if (webui) {
			return makeConfluenceURLPath(webui);
		}
		return null;
	},
	contentEdit: async ({ contentId }: { contentId: string }) => {
		const resp = await fetch(`/wiki/rest/api/content/${contentId}`);
		const {
			_links: { edituiv2 },
		} = await resp.json();
		if (!edituiv2) {
			return null;
		}
		return makeConfluenceURLPath(edituiv2);
	},
	spaceView: async ({ spaceKey }: { spaceKey: string }) => {
		return makeConfluenceURLPath(`/spaces/${spaceKey}`);
	},
	extension: async (
		{ moduleKey }: { moduleKey: string },
		{ getAppForgeUIExtensions }: TargetURLUtils,
	) => {
		const { extensions } = await getAppForgeUIExtensions({
			expandAppOwner: false,
		});
		if (!extensions) {
			return null;
		}
		const extension = extensions.find(({ key }) => key === moduleKey);
		if (!extension?.type) {
			return null;
		}
		const extensionUrlHelper = extensionURLHelpers[extension.type as ForgeExtensionPoints];
		if (!extensionUrlHelper) {
			return null;
		}
		return extensionUrlHelper(extension as FullForgeUIExtensionType);
	},
} as const;

type NavigationTargetHandlers = typeof navigationTargetHandlers;

type NavigateTargetInfo = Parameters<
	NavigationTargetHandlers[keyof NavigationTargetHandlers]
>[0] & {
	target: keyof NavigationTargetHandlers;
};

type NavigationTargetHandler = (
	data: Omit<NavigateTargetInfo, 'target'>,
	utils: TargetURLUtils,
) => Promise<string | null>;

export const createNavigateTargetUrl = async (
	targetInfo: NavigateTargetInfo,
	utils: TargetURLUtils,
) => {
	const { target, ...payload } = targetInfo;
	const handler = navigationTargetHandlers[target] as NavigationTargetHandler;

	if (!handler) {
		return null;
	}
	try {
		return await handler(payload, utils);
	} catch (e) {
		// TODO: review error handling
		// eslint-disable-next-line no-console
		console.error('Failed to create navigate target URL', e);
		return null;
	}
};
